import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";
import { MapLayerConfig } from "../types";
import { processLayerUrls } from "../utilities/processLayerUrls";
import { actionNameBuilder } from "../utilities/storeActionNameBuilder";
import { applyResetMiddleware } from "./middleware/resetMiddleware";
import { useShallow } from "zustand/react/shallow";

interface FailedLayerInfo {
  id: string;
  error: Error;
  statusCode: number;
}

interface LayerState {
  layers: MapLayerConfig[];
  visibleLayers: Set<string>;
  failedLayers: FailedLayerInfo[];
  layerRefreshTrigger: Record<string, number>;
  terrainLayers: MapLayerConfig[];
  basicAuthLayerUrls: string[];
  oAuthLayerUrls: string[];

  actions: {
    initializeLayers: (mapLayers: MapLayerConfig[]) => void;
    toggleLayerVisibility: (id: string) => void;
    setLayerVisibility: (id: string, visibility: boolean) => void;
    setFilterLayersVisibility: (visibility: boolean) => void;
    addFailedLayer: (id: string, error: Error, statusCode: number) => void;
    refreshLayer: (layerId: string) => void;
    reset: (mapLayers?: MapLayerConfig[]) => void;
    /** Replace all spatial-* layers with the provided set. Does not touch other layers. */
    mergeSpatialDatasetLayers: (spatialLayers: MapLayerConfig[]) => void;
    /** Update the opacity in-place for a single layer (0–1). */
    setLayerOpacity: (id: string, opacity: number) => void;
  };
}

const actionName = actionNameBuilder("Layer");

export const useLayerStore = create<LayerState>()(
  devtools(
    immer(
      applyResetMiddleware((set, get, store) => ({
        layers: [],
        terrainLayers: [],
        visibleLayers: new Set(),
        failedLayers: [],
        layerRefreshTrigger: {},
        basicAuthLayerUrls: [],
        oAuthLayerUrls: [],

        actions: {
          initializeLayers: (mapLayers) =>
            set(
              (state) => {
                const processed = processLayerUrls(mapLayers);

                // Preserve runtime visibility for layers that still exist.
                // Only fall back to initialVisibility for layers newly added
                // to the stack — prevents initializeLayers re-runs (triggered
                // by Fulcrum/CM-table layer refreshes) from wiping the active
                // spatial overlay or any user toggle. (§7.5 fix)
                const previouslyVisible = state.visibleLayers;
                const visible = new Set<string>();
                const staticLayerIds = new Set(processed.map((l) => l.id));
                processed.forEach((layer) => {
                  if (previouslyVisible.has(layer.id) || layer.initialVisibility) {
                    visible.add(layer.id);
                  }
                });
                // Also preserve any dynamic layer IDs (e.g., cm-table-*, spatial-active)
                // that were set visible at runtime but are not part of the static config.
                // Without this, initializeLayers wipes their visibility and
                // useCmTableMapLayers mistakenly treats it as an explicit user hide.
                previouslyVisible.forEach((id) => {
                  if (!staticLayerIds.has(id)) {
                    visible.add(id);
                  }
                });

                state.layers = processed;
                state.visibleLayers = visible;
                state.failedLayers = [];
                state.terrainLayers = processed.filter((layer) => layer.type === "raster-dem");
                state.basicAuthLayerUrls = processed.filter((layer) => layer.useBasicAuth).map((layer) => layer.url);
                state.oAuthLayerUrls = processed.filter((layer) => layer.useOAuth).map((layer) => layer.url);
              },
              undefined,
              actionName("initializeLayers")
            ),

          toggleLayerVisibility: (id) =>
            set(
              (state) => {
                state.visibleLayers.has(id) ? state.visibleLayers.delete(id) : state.visibleLayers.add(id);
              },
              undefined,
              actionName("toggleLayerVisibility")
            ),

          setLayerVisibility: (id, visibility) =>
            set(
              (state) => {
                visibility ? state.visibleLayers.add(id) : state.visibleLayers.delete(id);
              },
              undefined,
              actionName("setLayerVisibility")
            ),

          setFilterLayersVisibility: (visibility) =>
            set(
              (state) => {
                state.layers.forEach((layer) => {
                  if (layer.type === "geojson" && "filters" in layer) {
                    visibility ? state.visibleLayers.add(layer.id) : state.visibleLayers.delete(layer.id);
                  }
                });
              },
              undefined,
              actionName("setFilterLayersVisibility")
            ),

          addFailedLayer: (id, error, statusCode) =>
            set(
              (state) => {
                state.failedLayers.push({ id, error, statusCode });
                state.layers = state.layers.filter((l) => l.id !== id);
                state.visibleLayers.delete(id);
              },
              undefined,
              actionName("addFailedLayer")
            ),

          refreshLayer: (layerId) =>
            set(
              (state) => {
                state.layerRefreshTrigger[layerId] = (state.layerRefreshTrigger[layerId] || 0) + 1;
              },
              undefined,
              actionName("refreshLayer")
            ),

          reset: (mapLayers) =>
            set(
              () => {
                if (!mapLayers) {
                  return store.getInitialState();
                }

                const processed = processLayerUrls(mapLayers);
                const visible = new Set<string>();

                processed.forEach((layer) => {
                  if (layer.initialVisibility) visible.add(layer.id);
                });

                return {
                  layers: processed,
                  visibleLayers: visible,
                  failedLayers: [],
                  layerRefreshTrigger: {}
                };
              },
              undefined,
              actionName("reset")
            ),

          mergeSpatialDatasetLayers: (spatialLayers) =>
            set(
              (state) => {
                const withoutSpatial = state.layers.filter((l) => !l.id.startsWith("spatial-"));
                state.layers = [...withoutSpatial, ...spatialLayers];
              },
              undefined,
              actionName("mergeSpatialDatasetLayers")
            ),

          setLayerOpacity: (id, opacity) =>
            set(
              (state) => {
                const layer = state.layers.find((l) => l.id === id) as any;
                if (layer?.options) {
                  layer.options.opacity = opacity;
                }
              },
              undefined,
              actionName("setLayerOpacity")
            )
        }
      }))
    ),
    { name: "LayerStore" }
  )
);

export const useLayerSelector = () =>
  useLayerStore(
    useShallow((state) => ({
      layers: state.layers,
      visibleLayers: state.visibleLayers,
      failedLayers: state.failedLayers,
      layerRefreshTrigger: state.layerRefreshTrigger,
      terrainLayers: state.terrainLayers,
      basicAuthLayerUrls: state.basicAuthLayerUrls,
      oAuthLayerUrls: state.oAuthLayerUrls
    }))
  );

export const useLayerActions = () => useLayerStore((state) => state.actions);
